import { getGateAndLine, GATE_TO_CENTER } from '../data/gates.js';
import { CHANNELS } from '../data/channels.js';
import { createAstroTime, getPersonalityAndDesign, formatAstroTime } from './ephemeris.js';
import { calculateAstrology, enrichActivations, getZodiacSign, getChineseZodiac } from './astrology.js';

/**
 * Calculate a full Human Design chart from birth data.
 *
 * @param {string} dateStr – "YYYY-MM-DD"
 * @param {string} timeStr – "HH:MM"
 * @param {string} timezone – e.g. "CET" or "+1"
 * @returns {object} chart data
 */
export function calculateChart(dateStr, timeStr, timezone) {
  try {
    const birthTime = createAstroTime(dateStr, timeStr, timezone);
    const { personality, design, designTime } = getPersonalityAndDesign(birthTime);

    // Convert planet longitudes → gate.line activations
    const pAct = activationsFromPlanets(personality);  // personality activations
    const dAct = activationsFromPlanets(design);        // design activations

    // Unique gate numbers (just the gate, ignoring lines)
    const pGateNums = pAct.map(a => a.gate);
    const dGateNums = dAct.map(a => a.gate);
    const allGateNums = [...new Set([...pGateNums, ...dGateNums])];

    // Defined channels: both gates of a channel must be present (across personality + design)
    const definedChannels = CHANNELS.filter(([gA, gB]) =>
      allGateNums.includes(gA) && allGateNums.includes(gB)
    );

    // Defined centers: only through complete channels
    const definedCenters = centersFromChannels(definedChannels);

    // Type
    const type = determineType(definedCenters, definedChannels);

    // Authority
    const authority = determineAuthority(definedCenters, definedChannels);

    // Profile (Personality Sun line / Design Sun line)
    const pSunLine = pAct.find(a => a.planet === 'Sun')?.line ?? 1;
    const dSunLine = dAct.find(a => a.planet === 'Sun')?.line ?? 1;
    const profile = `${pSunLine}/${dSunLine}`;

    // Incarnation Cross (Personality Sun, Personality Earth, Design Sun, Design Earth)
    const crossGates = [
      pAct.find(a => a.planet === 'Sun')?.gate,
      pAct.find(a => a.planet === 'Earth')?.gate,
      dAct.find(a => a.planet === 'Sun')?.gate,
      dAct.find(a => a.planet === 'Earth')?.gate
    ];

    // Definition type (connectivity of defined centers)
    const definition = determineDefinition(definedChannels);

    // Variable / 4 arrows (Personality Sun, Design Sun, Personality North Node, Design North Node)
    const variable = determineVariable(pAct, dAct);

    // Zodiac signs from existing longitudes (no SwissEph needed)
    const pSunZodiac = getZodiacSign(personality.Sun.longitude);

    // Chinese zodiac from birth year
    const [year] = dateStr.split('-').map(Number);
    const chineseZodiac = getChineseZodiac(year);

    return {
      success: true,
      type,
      profile,
      authority,
      definition,
      incarnationCross: crossGates,
      variable,
      personalityActivations: pAct,
      designActivations: dAct,
      personalityGateNums: pGateNums,
      designGateNums: dGateNums,
      allGateNums,
      definedCenters,
      definedChannels,
      birthDate: formatAstroTime(birthTime),
      designDate: formatAstroTime(designTime),
      sunSign: pSunZodiac,
      chineseZodiac,
      // Store raw UTC date for astrology module
      _utcDate: birthTime.date
    };
  } catch (error) {
    console.error('Chart calculation error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Load extended astrology data (async, called after initial chart display)
 * Adds: Ascendant, MC, Chiron, Lilith, Retrograde markers, Zodiac signs
 */
export async function calculateAstrologyExtras(chart, lat, lon) {
  try {
    const astro = await calculateAstrology(chart._utcDate, lat, lon);

    // Enrich activations with zodiac + retrograde info
    const pActEnriched = enrichActivations(chart.personalityActivations, astro.planetRetrogrades);
    const dActEnriched = enrichActivations(chart.designActivations, astro.planetRetrogrades);

    return {
      ...chart,
      ascendant: astro.ascendant,
      mc: astro.mc,
      houses: astro.houses,
      chiron: astro.chiron,
      lilith: astro.lilith,
      planetRetrogrades: astro.planetRetrogrades,
      personalityActivations: pActEnriched,
      designActivations: dActEnriched,
      astroLoaded: true
    };
  } catch (err) {
    console.warn('Astrology extras failed (non-critical):', err);
    return { ...chart, astroLoaded: false };
  }
}

/* ------------------------------------------------------------------ */
/*  Internal helpers                                                  */
/* ------------------------------------------------------------------ */

/**
 * Convert planet positions → array of { planet, longitude, gate, line, center }
 */
function activationsFromPlanets(planets) {
  const PLANET_ORDER = [
    'Sun', 'Earth', 'Moon', 'NorthNode', 'SouthNode',
    'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn',
    'Uranus', 'Neptune', 'Pluto'
  ];
  const result = [];
  for (const key of PLANET_ORDER) {
    const p = planets[key];
    if (!p || p.longitude === undefined) continue;
    const { gate, line, color, tone, base } = getGateAndLine(p.longitude);
    result.push({
      planet: key,
      planetName: p.name,
      longitude: p.longitude,
      gate,
      line,
      color,
      tone,
      base,
      center: GATE_TO_CENTER[gate]
    });
  }
  return result;
}

/**
 * Determine which centers are defined (only through complete channels).
 */
function centersFromChannels(definedChannels) {
  const centers = new Set();
  for (const [gA, gB] of definedChannels) {
    centers.add(GATE_TO_CENTER[gA]);
    centers.add(GATE_TO_CENTER[gB]);
  }
  return [...centers];
}

/* =================== TYPE =================== */

const MOTOR_CENTERS = new Set(['root', 'sacral', 'solarplexus', 'ego']);

function determineType(definedCenters, definedChannels) {
  if (definedCenters.length === 0) return 'Reflektor';

  const sacralDefined = definedCenters.includes('sacral');
  const motorToThroat = hasMotorToThroatPath(definedChannels);

  if (sacralDefined && motorToThroat) return 'Manifestierender Generator';
  if (sacralDefined) return 'Generator';
  if (motorToThroat) return 'Manifestor';
  return 'Projektor';
}

/**
 * BFS: is there a connected path of defined channels from any motor center to the throat?
 * The path can go through intermediate centers.
 */
function hasMotorToThroatPath(definedChannels) {
  // Build adjacency graph of centers connected by defined channels
  const adj = buildCenterGraph(definedChannels);

  // BFS from each motor center to throat
  for (const motor of MOTOR_CENTERS) {
    if (adj.has(motor) && bfsReachable(adj, motor, 'throat')) {
      return true;
    }
  }
  return false;
}

function buildCenterGraph(definedChannels) {
  const adj = new Map();  // center → Set<center>
  for (const [gA, gB] of definedChannels) {
    const cA = GATE_TO_CENTER[gA];
    const cB = GATE_TO_CENTER[gB];
    if (!adj.has(cA)) adj.set(cA, new Set());
    if (!adj.has(cB)) adj.set(cB, new Set());
    adj.get(cA).add(cB);
    adj.get(cB).add(cA);
  }
  return adj;
}

function bfsReachable(adj, start, target) {
  const visited = new Set();
  const queue = [start];
  while (queue.length) {
    const cur = queue.shift();
    if (cur === target) return true;
    if (visited.has(cur)) continue;
    visited.add(cur);
    for (const neighbor of (adj.get(cur) || [])) {
      if (!visited.has(neighbor)) queue.push(neighbor);
    }
  }
  return false;
}

/* =================== AUTHORITY =================== */

function determineAuthority(definedCenters, definedChannels) {
  const has = (c) => definedCenters.includes(c);
  const adj = buildCenterGraph(definedChannels);

  // 1. Emotional (Solar Plexus defined)
  if (has('solarplexus')) return 'Emotionale Autorität';

  // 2. Sacral (Sacral defined, no Solar Plexus)
  if (has('sacral')) return 'Sakrale Autorität';

  // 3. Splenic (Spleen defined)
  if (has('spleen')) return 'Milz-Autorität';

  // 4. Ego manifested (Ego defined + path to Throat)
  if (has('ego') && bfsReachable(adj, 'ego', 'throat')) {
    return 'Ego manifestierte Autorität';
  }

  // 5. Ego projected (Ego defined, no path to Throat)
  if (has('ego')) return 'Ego projizierte Autorität';

  // 6. Self-projected (G defined + path to Throat)
  if (has('g') && bfsReachable(adj, 'g', 'throat')) {
    return 'Selbst-projizierte Autorität';
  }

  // 7. No defined centers at all → Lunar (Reflektor)
  if (definedCenters.length === 0) return 'Lunar Autorität';

  // 8. Mental / Environment authority
  return 'Umwelt-Autorität';
}

/* =================== DEFINITION TYPE =================== */

/**
 * Determine if the defined centers form:
 * - Keine Definition (Reflector)
 * - Einfache Definition (all connected in one group)
 * - Split Definition (two separate groups)
 * - Dreifach-Split (three groups)
 * - Vierfach-Split (four groups)
 */
function determineDefinition(definedChannels) {
  if (definedChannels.length === 0) return 'Keine Definition';

  const adj = buildCenterGraph(definedChannels);
  const allCenters = [...adj.keys()];

  // Count connected components via BFS
  const visited = new Set();
  let components = 0;

  for (const center of allCenters) {
    if (visited.has(center)) continue;
    components++;
    const queue = [center];
    while (queue.length) {
      const cur = queue.shift();
      if (visited.has(cur)) continue;
      visited.add(cur);
      for (const neighbor of (adj.get(cur) || [])) {
        if (!visited.has(neighbor)) queue.push(neighbor);
      }
    }
  }

  switch (components) {
    case 1: return 'Einfache Definition';
    case 2: return 'Split Definition';
    case 3: return 'Dreifach-Split Definition';
    case 4: return 'Vierfach-Split Definition';
    default: return `${components}-fach-Split Definition`;
  }
}

/* =================== VARIABLE (4 Arrows) =================== */

/**
 * The 4 arrows are determined by the color (1-6) of:
 * 1st arrow (top-left):  Design Sun – Digestion/Environment
 * 2nd arrow (top-right): Personality Sun – Perspective/Motivation
 * 3rd arrow (bottom-left): Design North Node – Environment
 * 4th arrow (bottom-right): Personality North Node – Awareness
 *
 * Color 1-3 → left-pointing arrow (←), Color 4-6 → right-pointing arrow (→)
 * For now we derive the line which determines the color at a high level.
 * Precise color/tone calculation requires sub-line data beyond basic gate.line.
 */
function determineVariable(pAct, dAct) {
  const pSun = pAct.find(a => a.planet === 'Sun');
  const dSun = dAct.find(a => a.planet === 'Sun');
  const pNode = pAct.find(a => a.planet === 'NorthNode');
  const dNode = dAct.find(a => a.planet === 'NorthNode');

  // Variable arrows are determined by the COLOR (1-6) of the activation:
  // Color 1-3 = left (←), Color 4-6 = right (→)
  const arrow = (color) => (color && color <= 3) ? '←' : '→';

  return {
    arrows: [
      arrow(dSun?.color),   // top-left: Design Sun
      arrow(pSun?.color),   // top-right: Personality Sun
      arrow(dNode?.color),  // bottom-left: Design Node
      arrow(pNode?.color)   // bottom-right: Personality Node
    ],
    details: {
      designSun: dSun,
      personalitySun: pSun,
      designNode: dNode,
      personalityNode: pNode
    }
  };
}
